import React, { memo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

const HeroContent = ({ heroState, getHeroTitle }) => {
  const intl = useIntl();
  const [isMobile, setIsMobile] = useState(false);

  // テキスト分割の最適化
  const isJapanese = intl.locale === 'ja';
  const isDesktop = window.innerWidth >= 1024;
  const isTablet = window.innerWidth >= 712 && window.innerWidth < 1024;

  let textLines;
  if (isMobile) {
    // モバイルの場合は自然な改行（改行文字で分割）
    textLines = heroState.displayedText.split('\n');
  } else if (isJapanese && (isDesktop || isTablet)) {
    // 日本語でデスクトップまたはタブレットの場合、「。」で改行
    const text = heroState.displayedText.replace(/\n/g, '');
    const sentences = text.split('。').filter(s => s.trim() !== '');
    if (sentences.length >= 2) {
      textLines = sentences.map(sentence => sentence.trim() + '。');
    } else {
      textLines = [text];
    }
  } else {
    textLines = [heroState.displayedText.replace(/\n/g, '')];
  }

  // 説明文のテキスト分割（句読点ハードコーディング・大画面時のみ適用）
  const descriptionText = intl.formatMessage({ id: 'hero.description' });
  let descriptionLines;
  const isAsianLanguage = [
    'ja',
    'ko',
    'zh-CN',
    'zh-TW',
    'zh-HK',
    'th',
  ].includes(intl.locale);

  if (isAsianLanguage && (isDesktop || isTablet)) {
    if (['zh-CN', 'zh-TW', 'zh-HK'].includes(intl.locale)) {
      // 中国語: 2個目の「，」の後で改行
      const text = descriptionText.replace(/\n/g, '');
      const parts = text.split('，').filter(s => s.trim() !== '');
      if (parts.length >= 3) {
        const firstLine = parts.slice(0, 2).join('，') + '，';
        const secondLine = parts.slice(2).join('，');
        descriptionLines = [firstLine, secondLine];
      } else {
        descriptionLines = [text];
      }
    } else if (intl.locale === 'ko') {
      // 韓国語: 「넘어, 전」のカンマで優先的に改行。次に2個目のカンマ、なければ1個目のカンマ、その後は句点ベース。
      const text = descriptionText.replace(/\n/g, '');
      const pattern = /넘어,\s*전/;
      const patternMatch = text.match(pattern);
      if (patternMatch) {
        const commaPos = text.indexOf(',', text.indexOf('넘어'));
        descriptionLines = [
          text.slice(0, commaPos + 1),
          text.slice(commaPos + 1).trim(),
        ];
      } else {
        const commaIdxs = [];
        for (let i = 0; i < text.length; i++)
          if (text[i] === ',' || text[i] === '，') commaIdxs.push(i);
        if (commaIdxs.length >= 2) {
          const splitAt = commaIdxs[1];
          descriptionLines = [
            text.slice(0, splitAt + 1),
            text.slice(splitAt + 1).trim(),
          ];
        } else if (commaIdxs.length === 1) {
          const splitAt = commaIdxs[0];
          descriptionLines = [
            text.slice(0, splitAt + 1),
            text.slice(splitAt + 1).trim(),
          ];
        } else {
          const sentences = text.split(/[。.]/).filter(s => s.trim() !== '');
          if (sentences.length >= 2) {
            descriptionLines = sentences.map((s, idx) =>
              idx < sentences.length - 1
                ? s.trim() +
                  (descriptionText.includes(s.trim() + '。') ? '。' : '.')
                : s.trim()
            );
          } else {
            descriptionLines = [text];
          }
        }
      }
    } else {
      // 日本語・タイ語など: 「。」で分割
      const text = descriptionText.replace(/\n/g, '');
      const sentences = text.split('。').filter(s => s.trim() !== '');
      if (sentences.length >= 2) {
        descriptionLines = sentences.map(s => s.trim() + '。');
      } else {
        descriptionLines = [text];
      }
    }
  } else {
    // 非アジア言語: 大画面のみ\nで分割
    if (isDesktop || isTablet) {
      descriptionLines = descriptionText
        .split('\n')
        .filter(line => line.trim() !== '');
    } else {
      descriptionLines = [descriptionText];
    }
  }

  // 画面サイズの監視
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 480);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 実測幅適用は一旦無効化（レイアウトシフト回避のため）

  return (
    <div className="relative z-40 flex flex-col justify-center items-center h-full text-white px-4">
      <div className="text-center max-w-4xl sm:max-w-5xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-8xl mx-auto">
        <h1 className="mb-6 2xl:mb-8 3xl:mb-8 text-center w-full">
          <span
            className={`hero-title hero-title-xs sm:hero-title-sm md:hero-title-md lg:hero-title-lg xl:hero-title-xl 2xl:hero-title-2xl hero-title-leading-xs sm:hero-title-leading-sm md:hero-title-leading-md lg:hero-title-leading-lg xl:hero-title-leading-xl 2xl:hero-title-leading-2xl hero-title-max-w-xs sm:hero-title-max-w-sm md:hero-title-max-w-md lg:hero-title-max-w-lg xl:hero-title-max-w-xl 2xl:hero-title-max-w-2xl ${
              isMobile ? 'hero-title-mobile' : ''
            } ${
              isJapanese && (isDesktop || isTablet)
                ? 'hero-title-japanese-desktop'
                : ''
            }`}
            style={{
              textShadow: `
              0 1px 2px rgba(0,0,0,0.1),
              0 2px 4px rgba(0,0,0,0.1),
              0 4px 8px rgba(0,0,0,0.05)
            `,
            }}
          >
            {textLines.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < textLines.length - 1 && <br />}
              </React.Fragment>
            ))}
            {!heroState.isTypingComplete && heroState.showCursor && (
              <span className="animate-pulse">|</span>
            )}
          </span>
        </h1>
        <p
          className={`font-body text-base sm:text-lg lg:text-xl xl:text-xl 2xl:text-2xl 3xl:text-2xl text-center text-white text-opacity-80 transition-all duration-1000 ease-out font-normal w-full ${
            heroState.showDescription
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={{
            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            lineHeight: '1.8',
          }}
        >
          {descriptionLines.map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < descriptionLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      </div>
    </div>
  );
};

export default memo(HeroContent);
